import { studentFragment, subjectFragment } from "modules/CommonFragments";
import {
  courseFragment,
  courseSubjectGroupFragment,
  courseSubjectFragment,
  courseCurriuclumProgramFragment,
  genericTagFragment,
} from "./CourseFragments";
import { singleCourseV2Fragment } from "Teacher/modules/TeacherFragments";
import { gql } from "lib/apolloClient";

export const getGradeDetailsQuery = gql`
  query getGradeDetails($ids: [ID!]) {
    multiNode(ids: $ids, type: GRADE) {
      id
      ... on Grade {
        id
        name
        subjects(filters: { archivalState: ALL }) {
          id
          name
          icon
          grades {
            id
            name
          }
          isArchived
          globalSubject
          framework
          snsChildren
          standardSet {
            id
            title
            description
            code
          }
          subjectLevelLabels {
            cuLevelName
            strandLevelName
            loLevelName
          }
          type
          tags {
            id
            key
            value
          }
          variants {
            level {
              ...basicGenericTagItem
            }
            responseLanguages {
              ...basicGenericTagItem
            }
          }
          flatVariants(filters: { archivalState: ALL }) {
            isArchived
            level {
              ...basicGenericTagItem
            }
            responseLanguage {
              ...basicGenericTagItem
            }
          }
          subjectGroup {
            id
            name
          }
        }
      }
    }
  }
  ${genericTagFragment.genericTag}
`;

export const getOrganizationCoursesStudents = gql`
  query getCourseStudentGroup(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(filters: { searchText: $searchText, grades: $grades }) {
          totalCount
          edges {
            node {
              id
              title
              collaborators: students {
                totalCount
                edges {
                  node {
                    ...studentItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
`;

export const getCourseStudentsFoldersQuery = gql`
  query getCourseDetails($id: ID!, $folderType: FOLDER_TYPE_ENUM!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseFragment.courseStudentsFolders}
`;

export const getMultiCourseStudentsFoldersQuery = gql`
  query getMultipleCourseDetails($ids: [ID!], $folderType: FOLDER_TYPE_ENUM!) {
    multiNode(ids: $ids, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseFragment.courseStudentsFolders}
`;

export const getCourseDetailsQuery = gql`
  query getCourseDetails(
    $id: ID!
    $courseGradeFilters: CourseGradeFilters
    $collaboratorFilters: CollaboratorFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseDetailsItem
      }
    }
  }
  ${courseFragment.courseDetails}
`;

export const getCourseCollaboratorQuery = gql`
  query getCourseCollaborator($id: ID!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseFragment.courseCollaborator}
`;

export const getOrganizationTeachersCountQuery = gql`
  query getOrganizationTeachersCountQuery(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        staff(filters: $filters) {
          userStatusCount {
            status
            totalCount
          }
          userCount {
            role
            totalCount
          }
          totalCount
        }
      }
    }
  }
`;

export const getCourseGenericFoldersQuery = gql`
  query getCourseGenericFolders(
    $courseId: ID!
    $folderType: FOLDER_TYPE_ENUM!
    $filters: TypeDetailsFilterInput
    $assignmentFilters: AssignmentFeedFilters
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseFragment.courseGenericFoldersDetailsFragment}
`;

export const getCourseExtraDetailsQuery = gql`
  query getCourseExtraDetailsQuery($id: ID!, $filters: CourseFilter) {
    node(id: $id, type: COURSE) {
      ... on Course {
        id
        students(filters: $filters) {
          edges {
            node {
              id
            }
          }
        }
        weeklyPlannerTemplatesCount
        ...courseUniPlansItem
        ...courseStudentFoldersItem
        ...courseAssesmentEvaluationCountItem
      }
    }
  }
  ${courseFragment.courseUnitPlansCountFragment}
  ${courseFragment.courseStudentFoldersCountFragment}
  ${courseFragment.courseAssesmentEvaluationCountFragment}
`;

export const getCourseSubjectDetailsQuery = gql`
  query getSubjectsForCourse($id: ID!, $filters: SubjectFilterInput) {
    node(id: $id, type: COURSE) {
      ... on Course {
        id
        subjects(filters: $filters) {
          ...subjectItem
          icon
        }
      }
    }
  }
  ${subjectFragment.basicSubjectDetails}
`;

export const getSubjectCoursesQuery = gql`
  query getSubjectCourses(
    $id: ID!
    $subjectCourseFilters: SubjectCourseFilters
  ) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        name
        isArchived
        courses(filters: $subjectCourseFilters) {
          edges {
            node {
              id
              title
              primaryGrade {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCoursesV2Query = gql`
  query getUserCoursesV2(
    $userId: ID!
    $first: Int
    $after: String
    $courseFilters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
    $archivedCourseFilters: CourseFilter
  ) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        archivedCourses: coursesV2(filters: $archivedCourseFilters) {
          totalCount
        }
        coursesV2(first: $first, after: $after, filters: $courseFilters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Course {
                ...courseV2
              }
            }
          }
        }
      }
    }
  }
  ${singleCourseV2Fragment.singleCourseV2}
`;

export const getUserCoursesBasicDetailsQuery = gql`
  query getUserCoursesBasicDetails($userId: ID!, $courseFilters: CourseFilter) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        coursesV2(filters: $courseFilters) {
          edges {
            node {
              ... on Course {
                id
                title
                curriculumProgram {
                  id
                  type
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $userId: ID!
    $courseFilters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
    $projectGroupFilters: ProjectGroupFilter
  ) {
    node(id: $userId, type: STUDENT) {
      id
      ... on Student {
        courses(filters: $courseFilters) {
          id
          title
          profileImage
          isArchived
          isBookmark
          isDemo
          profileImageData {
            url
            icon
          }
          genericTags(filters: { types: SUBJECT_LEVEL }) {
            id
            label
            type
          }
          projectGroups(filters: $projectGroupFilters) {
            id
            name
            type
            subType
            curriculumProgram {
              id
              type
            }
          }
          primaryGrade(filters: $courseGradeFilters) {
            id
            name
          }
          grades(filters: $courseGradeFilters) {
            id
            name
          }
          learningCourse {
            id
            title
            academicCourse {
              id
              label
            }
            displayMetadata
          }
          ...courseSubjectGroupItem
          ...courseSubjectItem
          ...courseCurriculumProgramItem
          ...courseUniPlansItem
          ...courseStudentFoldersItem
          ...courseAssesmentEvaluationCountItem
        }
      }
    }
  }
  ${courseFragment.courseUnitPlansCountFragment}
  ${courseFragment.courseStudentFoldersCountFragment}
  ${courseFragment.courseAssesmentEvaluationCountFragment}
  ${courseSubjectGroupFragment.basicSubjectGroupDetails}
  ${courseSubjectFragment.basicSubjectDetails}
  ${courseCurriuclumProgramFragment.basicCurriculumProgramDetails}
`;

export const getUserCoursesBasicDetailsWihoutPaginationQuery = gql`
  query getUserCoursesBasicDetailsWihoutPaginationQuery(
    $userId: ID!
    $courseFilters: CourseFilter
  ) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        id
        coursesV2(
          orderBy: TITLE
          orderByDirection: ASC
          filters: $courseFilters
        ) {
          edges {
            node {
              id
              title
              isBookmark
              profileImageData {
                url
                icon
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCourseAccessQuery = gql`
  query getUserCourseAccess($id: ID!, $filters: CourseFilter) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        coursesV2(filters: $filters) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;
